import {Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {MaterialModule} from "../../../../material.module";
import {
  ICustomFontConfigurationValidationResult,
  validateAndNormalizeImageCompositionOptions,
  validateCustomFontConfiguration
} from "../../../../models/chatbot-options.model";

import {MessagesSettingsComponent} from "../messages-settings/messages-settings.component";
import {Subscription} from 'rxjs';
import {DisabledButtonReasonComponent} from "../../../shared/disabled-button-reason/disabled-button-reason.component";
import {ColorFieldComponent} from '../../../../shared/color-field/color-field.component';
import {environment} from '../../../../../environments/environment';
import {auditTime} from 'rxjs/operators';
import {ProductCardTextSettingsComponent} from '../product-card-text-settings/product-card-text-settings.component';
import {PRODUCT_CARD_TEXT_FIELDS, ProductCardTexts, ProductCardTextOverrides} from '../../../../models/product-card-texts.model';
import {FigmaProductCardSettingsComponent} from '../figma-product-card-settings/figma-product-card-settings.component';
import {FigmaStylingMode} from '../../../../models/figma-product-card.model';
import {FigmaDesignSnapshot, FigmaDesignView} from '../../../../models/figma-chat-design.model';
import {QuickStartEditorComponent} from '../quick-start-editor/quick-start-editor.component';

interface VisibilityRule {
  path?: string;
  equals?: any;
  matches?: () => boolean;
}

interface ColorControlConfig {
  key: string;
  label: string;
  hint?: string;
  requiresAdvanced?: boolean;
  visibleWhen?: VisibilityRule;
  hover?: {
    key: string;
    label?: string;
    hint?: string;
  };
}

interface ColorGroupConfig {
  title: string;
  description?: string;
  requiresAdvanced?: boolean;
  visibleWhen?: VisibilityRule;
  controls: ColorControlConfig[];
}

@Component({
  selector: 'app-frontend-settings',
  templateUrl: './frontend-settings.component.html',
  styleUrls: ['./frontend-settings.component.scss'],
  standalone: true,
    imports: [MaterialModule, ReactiveFormsModule, MessagesSettingsComponent, DisabledButtonReasonComponent, ColorFieldComponent, ProductCardTextSettingsComponent, FigmaProductCardSettingsComponent, QuickStartEditorComponent]
})
export class FrontendSettingsComponent implements OnInit, OnDestroy, OnChanges {

  @Input() frontendForm: FormGroup;
  @Input() settingsForm!: FormGroup;
  @Input() messagesForm!: FormGroup;
  @Input() localizationForm!: FormGroup;
  @Input() localizationVariantsForm!: FormArray;
  @Input() isLocalizing = false;
  @Input() chatbotId: string;
  @Input() clientId = '';
  figmaStylingMode: FigmaStylingMode = 'standard';
  private figmaSnapshot: FigmaDesignSnapshot | null = null;
  productCardFigmaView = 'Desktop/Conversation';

  get productCardFigmaViews(): FigmaDesignView[] {
    return this.figmaSnapshot?.views.filter(view => view.name.endsWith('/Conversation')) ?? [];
  }
  get currentProductCardFigmaView(): FigmaDesignView | undefined {
    return this.productCardFigmaViews.find(view => view.name === this.productCardFigmaView);
  }
  get productCardPreviewWidth(): number | null {
    const width = this.currentProductCardFigmaView?.elements['window']?.style.width;
    return this.figmaStylingMode === 'experimental' && width ? Math.min(4096, width) : null;
  }
  get productCardPreviewHeight(): number | null {
    const height = this.currentProductCardFigmaView?.elements['card']?.style.height;
    return this.figmaStylingMode === 'experimental' && height ? Math.min(4096, height) + 64 : null;
  }
  onFigmaModeChange(mode: FigmaStylingMode): void {
    this.figmaStylingMode = mode;
    this.postProductCardPreviewConfig();
  }
  onFigmaSnapshotChange(snapshot: FigmaDesignSnapshot | null): void {
    this.figmaSnapshot = snapshot;
    if (!this.currentProductCardFigmaView) this.productCardFigmaView = this.productCardFigmaViews[0]?.name ?? 'Desktop/Conversation';
    this.postProductCardPreviewConfig();
  }
  selectProductCardFigmaView(name: string): void {
    this.productCardFigmaView = name;
    this.postProductCardPreviewConfig();
  }
  @Input() aiProductCardsEnabled = false;
  @Input() expandProductCardsPanel = false;
  @Input() validationTargetPath: string | null = null;
  @Input() imageCompositionRuntimeReady = false;
  @Input() expandedPanel: 'localization' | 'header-capabilities' | null = null;
  @Input() allowGenerativeActions = true;
  @Output() generateLocalization = new EventEmitter<void>();

  private readonly subscriptions = new Subscription();
  private readonly sanitizer = inject(DomSanitizer);
  private productCardPreviewFrame: HTMLIFrameElement | null = null;
  private productCardPreviewRequestSequence = 0;
  productCardDefaultTexts: ProductCardTexts = {};
  private productCardDefaultTextLocale = '';

  get productCardTextOverridesControl(): FormControl<ProductCardTextOverrides> | null {
    return this.frontendForm?.get('productCardSettings.productCardTextOverrides') as FormControl<ProductCardTextOverrides> | null;
  }

  get productCardTextLocale(): string {
    return this.localizationForm?.get('defaultLocale')?.value || 'en-US';
  }

  get productCardTextLocaleLabel(): string {
    return this.localeOptions.find(option => option.value === this.productCardTextLocale)?.display
      || this.productCardTextLocale;
  }
  private readonly productCardPreviewMessageHandler = (event: MessageEvent): void => {
    if (!this.productCardPreviewFrame?.contentWindow
      || event.source !== this.productCardPreviewFrame.contentWindow
      || event.origin !== this.productCardPreviewOrigin) {
      return;
    }
    const type = `${event.data?.type ?? ''}`;
    if (type === 'shine.product-card-preview.ready.v1') {
      this.postProductCardPreviewConfig();
    } else if (type === 'shine.product-card-preview.rendered.v1') {
      if (event.data?.requestId === `backoffice-preview-${this.productCardPreviewRequestSequence}` &&
          event.data?.cardLocale === this.productCardTextLocale) {
        const labels = event.data?.defaultLabels;
        this.productCardDefaultTexts = Object.fromEntries(PRODUCT_CARD_TEXT_FIELDS
          .filter(field => typeof labels?.[field.key] === 'string' && labels[field.key].length <= 200)
          .map(field => [field.key, labels[field.key]]));
        this.productCardDefaultTextLocale = event.data.cardLocale;
      }
      this.productCardPreviewStatus = 'Preview updated with the real widget renderer.';
    } else if (type === 'shine.product-card-preview.rejected.v1') {
      this.productCardPreviewStatus = 'Preview rejected an invalid configuration and kept its safe defaults.';
    }
  };
  headerLogoPreview: string | null = null;
  chatAvatarPreview: string | null = null;
  productCardPreviewStatus = 'Loading the real widget renderer…';
  private readonly productCardPreviewUrlValue = this.buildProductCardPreviewUrl();
  readonly productCardPreviewUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    this.productCardPreviewUrlValue
  );

  cardTypeOptions = [
    { key: 'car', value: 'Car' },
    { key: 'product-no-price', value: 'Product without price' },
    { key: 'product', value: 'Product' },
  ];

  productCardPresentationOptions = [
    { key: 'horizontal', value: 'Horizontal' },
    { key: 'horizontal-compact', value: 'Horizontal compact' },
    { key: 'vertical', value: 'Vertical' },
    { key: 'vertical-list', value: 'Vertical list' },
    { key: 'vertical-carousel', value: 'Vertical carousel' },
    { key: 'compact-carousel', value: 'Compact carousel' },
  ];

  get productCardPresentationMode(): string {
    return `${this.frontendForm?.get('productCardSettings.productCardPresentationMode')?.value ?? 'fixed'}`;
  }

  get productCardPreviewPresentation(): string {
    const requested = `${this.frontendForm?.get('productCardSettings.productCardPresentation')?.value ?? 'horizontal'}`;
    return this.productCardPresentationOptions.some(option => option.key === requested) ? requested : 'horizontal';
  }

  private buildProductCardPreviewUrl(): string {
    try {
      const configuredUrl = environment.frontendDummy || environment.widgetScriptUrl || window.location.origin;
      const url = new URL(configuredUrl, window.location.origin);
      const basePath = url.pathname.replace(/(?:generic|chatbot)(?:\/index\.html|\.js)?\/?$/i, '');
      url.pathname = `${basePath.replace(/\/?$/, '/')}product-card-preview/index.html`;
      url.search = '';
      url.hash = '';
      url.searchParams.set('parentOrigin', window.location.origin);
      // Escape HTML cached before the widget introduced hashed assets/cache headers.
      url.searchParams.set('assets', 'content-hash-v1');
      return url.toString();
    } catch {
      return 'about:blank';
    }
  }

  get productCardCssCollisionSelectors(): string[] {
    const css = `${this.frontendForm?.get('css')?.value ?? ''}`;
    const selectors = [
      '.product-card', '.card-image', '.card-info', '.details', '.price', '.rating',
      '.shine-btn', '.material-symbols-outlined', '--shine-product-card-'
    ];
    return selectors.filter(selector => css.includes(selector));
  }

  layoutPresetOptions = [
    { key: '', value: 'Default' },
    { key: 'compact-mobile', value: 'Compact mobile' },
  ];

  sampleMessagesPresentationOptions = [
    { key: '', value: 'Default grid' },
    { key: 'list', value: 'List' },
    { key: 'image-grid', value: 'Image grid' },
    { key: 'image-card-grid', value: '2x2 image card grid' },
  ];

  materialIconOptions = [
    { key: '', value: 'Default' },
    { key: 'arrow_upward_alt', value: 'Arrow up' },
    { key: 'restart_alt', value: 'Reset arrow' },
    { key: 'attach_file', value: 'Attach file' },
    { key: 'mic', value: 'Microphone' },
    { key: 'search', value: 'Search' },
    { key: 'edit_square', value: 'Edit square' },
    { key: 'send', value: 'Send' },
    { key: 'north', value: 'North arrow' },
    { key: 'refresh', value: 'Refresh' },
    { key: 'autorenew', value: 'Auto renew' }
  ];

  fontOptions = [
    { key: 'Inter Tight', value: 'Inter+Tight' },
    { key: 'Roboto', value: 'Roboto' },
    { key: 'Open Sans', value: 'Open+Sans' },
    { key: 'Lato', value: 'Lato' },
    { key: 'Montserrat', value: 'Montserrat' },
    { key: 'Poppins', value: 'Poppins' },
    { key: 'Ubuntu', value: 'Ubuntu' },
    { key: 'Raleway', value: 'Raleway' },
    { key: 'Nunito', value: 'Nunito' },
    { key: 'Source Sans Pro', value: 'Source+Sans+Pro' }
  ];

  get generateLocalizationDisabled(): boolean {
    return this.generateLocalizationDisabledReason.length > 0;
  }

  get generateLocalizationDisabledReason(): string {
    if (this.isLocalizing) return 'Localization generation is in progress.';
    if (!this.localizationForm?.get('defaultLocale')?.value) {
      return 'Choose a default locale before generating translations.';
    }
    return '';
  }

  fontProviderOptions = [
    { key: 'Google Fonts', value: 'google' },
    { key: 'Built-in / hosted custom font', value: 'custom' },
    { key: 'System font stack', value: 'system' }
  ];

  customFontPresetOptions = [
    { key: 'None - use hosted font URLs below', value: '' },
    { key: 'Kari Traa - Haffer SQ', value: 'kari-traa-haffer-sq' },
    { key: 'Motorpool - Land Rover Wide', value: 'motorpool-land-rover-wide' }
  ];

  localeOptions: { value: string, display: string }[] = [
    { value: 'af-ZA', display: 'Afrikaans (South Africa) - af-ZA' },
    { value: 'ar-SA', display: 'Arabic (Saudi Arabia) - ar-SA' },
    { value: 'bg-BG', display: 'Bulgarian (Bulgaria) - bg-BG' },
    { value: 'ca-ES', display: 'Catalan (Spain) - ca-ES' },
    { value: 'cs-CZ', display: 'Czech (Czech Republic) - cs-CZ' },
    { value: 'da-DK', display: 'Danish (Denmark) - da-DK' },
    { value: 'de-DE', display: 'German (Germany) - de-DE' },
    { value: 'el-GR', display: 'Greek (Greece) - el-GR' },
    { value: 'en-US', display: 'English (United States) - en-US' },
    { value: 'es-ES', display: 'Spanish (Spain) - es-ES' },
    { value: 'et-EE', display: 'Estonian (Estonia) - et-EE' },
    { value: 'fi-FI', display: 'Finnish (Finland) - fi-FI' },
    { value: 'fr-FR', display: 'French (France) - fr-FR' },
    { value: 'he-IL', display: 'Hebrew (Israel) - he-IL' },
    { value: 'hi-IN', display: 'Hindi (India) - hi-IN' },
    { value: 'hr-HR', display: 'Croatian (Croatia) - hr-HR' },
    { value: 'hu-HU', display: 'Hungarian (Hungary) - hu-HU' },
    { value: 'id-ID', display: 'Indonesian (Indonesia) - id-ID' },
    { value: 'it-IT', display: 'Italian (Italy) - it-IT' },
    { value: 'ja-JP', display: 'Japanese (Japan) - ja-JP' },
    { value: 'ko-KR', display: 'Korean (South Korea) - ko-KR' },
    { value: 'lt-LT', display: 'Lithuanian (Lithuania) - lt-LT' },
    { value: 'lv-LV', display: 'Latvian (Latvia) - lv-LV' },
    { value: 'ms-MY', display: 'Malay (Malaysia) - ms-MY' },
    { value: 'nb-NO', display: 'Norwegian Bokmål (Norway) - nb-NO' },
    { value: 'nl-NL', display: 'Dutch (Netherlands) - nl-NL' },
    { value: 'pl-PL', display: 'Polish (Poland) - pl-PL' },
    { value: 'pt-BR', display: 'Portuguese (Brazil) - pt-BR' },
    { value: 'ro-RO', display: 'Romanian (Romania) - ro-RO' },
    { value: 'ru-RU', display: 'Russian (Russia) - ru-RU' },
    { value: 'sk-SK', display: 'Slovak (Slovakia) - sk-SK' },
    { value: 'sl-SI', display: 'Slovenian (Slovenia) - sl-SI' },
    { value: 'sv-SE', display: 'Swedish (Sweden) - sv-SE' },
    { value: 'th-TH', display: 'Thai (Thailand) - th-TH' },
    { value: 'tr-TR', display: 'Turkish (Turkey) - tr-TR' },
    { value: 'uk-UA', display: 'Ukrainian (Ukraine) - uk-UA' },
    { value: 'vi-VN', display: 'Vietnamese (Vietnam) - vi-VN' },
    { value: 'zh-CN', display: 'Chinese (Simplified) - zh-CN' },
    { value: 'zh-TW', display: 'Chinese (Traditional) - zh-TW' }
  ];

  ngOnInit(): void {
    if (!this.frontendForm) {
      throw new Error('frontendForm Input is required for FrontendSettingsComponent');
    }

    this.syncImageCompositionControlState();
    window.addEventListener('message', this.productCardPreviewMessageHandler);
    this.subscriptions.add(
      this.frontendForm.valueChanges.pipe(auditTime(50)).subscribe(() => this.postProductCardPreviewConfig())
    );
    this.subscriptions.add(
      this.localizationForm?.get('defaultLocale')?.valueChanges.subscribe(() => {
        this.productCardDefaultTexts = {};
        this.postProductCardPreviewConfig();
      })
    );

    const headerLogoControl = this.frontendForm.get('generalSettings.logo');
    if (headerLogoControl) {
      this.updateHeaderLogoPreview(headerLogoControl.value);
      this.subscriptions.add(
        headerLogoControl.valueChanges.subscribe(value => this.updateHeaderLogoPreview(value))
      );
    }

    const inputShadowControl = this.stylingGroup?.get('inputShadowEnabled');
    if (inputShadowControl) {
      this.subscriptions.add(
        inputShadowControl.valueChanges.subscribe((enabled: boolean) => this.handleInputShadowToggle(enabled === true))
      );
      this.handleInputShadowToggle(inputShadowControl.value === true);
    }

    const chatAvatarControl = this.frontendForm.get('chatAvatar');
    if (chatAvatarControl) {
      this.updateChatAvatarPreview(chatAvatarControl.value);
      this.subscriptions.add(
        chatAvatarControl.valueChanges.subscribe(value => this.updateChatAvatarPreview(value))
      );
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.productCardPreviewMessageHandler);
    this.productCardPreviewFrame = null;
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] || changes['chatbotId']) this.onFigmaSnapshotChange(null);
    if (this.frontendForm) {
      this.syncImageCompositionControlState();
    }
  }

  get stylingGroup(): FormGroup {
    return this.frontendForm.get('styling') as FormGroup;
  }

  get customFontConfigurationValidation(): ICustomFontConfigurationValidationResult {
    return validateCustomFontConfiguration(this.stylingGroup?.getRawValue());
  }

  onProductCardPreviewLoad(frame: HTMLIFrameElement): void {
    this.productCardPreviewFrame = frame;
    this.productCardPreviewStatus = 'Updating the real widget preview…';
    this.postProductCardPreviewConfig();
  }

  private get productCardPreviewOrigin(): string {
    try {
      return new URL(this.productCardPreviewUrlValue).origin;
    } catch {
      return '';
    }
  }

  private postProductCardPreviewConfig(): void {
    if (!this.frontendForm) {
      return;
    }
    const target = this.productCardPreviewFrame?.contentWindow;
    const targetOrigin = this.productCardPreviewOrigin;
    if (!target || !targetOrigin || targetOrigin === 'null') {
      return;
    }
    if (this.productCardDefaultTextLocale !== this.productCardTextLocale) {
      this.productCardDefaultTexts = {};
    }

    const rawStyling = this.stylingGroup?.getRawValue() ?? {};
    const stylingKeys = [
      'ctaPrimaryBackground', 'ctaPrimaryHover', 'ctaPrimaryText', 'ctaPrimaryTextHover',
      'cardBtnBgColor', 'cardBtnBgColorHover', 'cardBtnColor', 'cardBtnColorHover',
      'cardBtnBorderColor', 'cardBtnBorderColorHover', 'cardAddToCartBgColor',
      'cardAddToCartBgColorHover', 'cardAddToCartColor', 'cardAddToCartColorHover',
      'cardAddToCartBorderColor', 'cardAddToCartBorderColorHover', 'cardWidth', 'cardMaxWidth',
      'cardBorderColor', 'cardBackgroundColor', 'cardPadding', 'cardTitleFontSize',
      'cardTitleFontWeight', 'cardDescriptionFontSize', 'cardDescriptionVisible', 'cardCtaVisible',
      'cardPriceVisible', 'cardPriceFontSize', 'cardPriceFontWeight', 'cardPriceCurrentColor',
      'cardPriceSaleCurrentColor', 'cardPriceOriginalColor', 'cardPriceOriginalFontSize',
      'cardImageAspectRatio', 'cardCompactImageMaxHeight', 'cardImageBackgroundColor',
      'cardBorderRadius', 'cardBadgeBackgroundColor', 'cardBadgeTextColor', 'cardSaleBadgeTextColor',
      'cardAttributeBackgroundColor', 'cardAttributeTextColor'
    ];
    const styling = Object.fromEntries(stylingKeys
      .map(key => [key, rawStyling[key]])
      .filter(([, value]) => typeof value === 'string' || typeof value === 'boolean'));
    const layoutPreset = `${this.frontendForm.get('generalSettings.layoutPreset')?.value ?? ''}` === 'compact-mobile'
      ? 'compact-mobile'
      : '';

    const view = this.currentProductCardFigmaView;
    const content = this.figmaSnapshot?.preview?.views.find(item => item.name === view?.name) ?? null;
    const imageIds = new Set([content?.logo, content?.feedback, ...(content?.products.map(item => item.image) ?? []),
      ...(content?.quickReplies.map(item => item.image) ?? [])]);
    const images = Object.fromEntries(Object.entries(this.figmaSnapshot?.preview?.images ?? {}).filter(([id]) => imageIds.has(id)));
    const figmaMode = this.figmaStylingMode === 'experimental';
    // Visibility is a product capability choice; Figma supplies cosmetic styling only.
    const figmaVisibility = Object.fromEntries(['cardDescriptionVisible', 'cardPriceVisible', 'cardCtaVisible']
      .filter(key => typeof rawStyling[key] === 'boolean')
      .map(key => [key, rawStyling[key]]));

    target.postMessage({
      type: 'shine.product-card-preview.configure.v1',
      requestId: `backoffice-preview-${++this.productCardPreviewRequestSequence}`,
      config: {
        designMode: figmaMode ? 'figma' : 'standard',
        figma: figmaMode && view ? {type: 'shine.figma-preview.configure.v1', view, content, images} : null,
        presentation: this.productCardPreviewPresentation,
        addToCartEnabled: this.frontendForm.get('productCardSettings.addToCartEnabled')?.value === true,
        cardLocale: this.productCardTextLocale,
        cardCurrency: `${this.frontendForm.get('productCardSettings.cardCurrency')?.value ?? ''}`,
        ...(this.productCardTextOverridesControl ? {productCardTextOverrides: this.productCardTextOverridesControl.value} : {}),
        productCardDetailsLabel: `${this.frontendForm.get('productCardSettings.productCardDetailsLabel')?.value || ''}`.trim(),
        productCardTestDriveLabel: `${this.frontendForm.get('productCardSettings.productCardTestDriveLabel')?.value || ''}`.trim(),
        layoutPreset,
        styling: figmaMode ? figmaVisibility : styling,
        customCss: figmaMode ? '' : `${this.frontendForm.get('css')?.value ?? ''}`
      }
    }, targetOrigin);
  }

  get localizationVariantGroups(): FormGroup[] {
    return (this.localizationVariantsForm?.controls ?? []) as FormGroup[];
  }

  get defaultSampleMessages(): FormArray {
    return this.messagesForm.get('sampleMessages') as FormArray;
  }

  getLocalizationSampleMessages(variant: FormGroup): FormArray {
    return variant.get('sampleMessages') as FormArray;
  }

  get defaultLocaleLabel(): string {
    const locale = `${this.localizationForm?.get('defaultLocale')?.value ?? ''}`.trim();
    return locale || 'Default language';
  }

  get aiProductCardsEnabledControl(): FormControl<boolean> {
    return this.settingsForm.get('aiProductCardsEnabled') as FormControl<boolean>;
  }

  get aiProductCardsInstructionControl(): FormControl<string> {
    return this.settingsForm.get('aiProductCardsInstruction') as FormControl<string>;
  }

  get imageCompositionGroup(): FormGroup {
    return this.frontendForm.get('imageComposition') as FormGroup;
  }

  get imageCompositionValidation() {
    return validateAndNormalizeImageCompositionOptions(
      this.imageCompositionGroup?.getRawValue(),
      this.imageCompositionGroup?.getRawValue(),
      this.frontendForm.get('generalSettings.attachmentsEnabled')?.value === true,
      this.imageCompositionRuntimeReady
    );
  }

  get expandLocalizationPanel(): boolean {
    return this.expandedPanel === 'localization'
      || this.validationTargetPath?.startsWith('messagesForm.sampleMessages.') === true
      || this.validationTargetPath?.startsWith('localizationVariantsForm.') === true;
  }

  isLocalizationVariantValidationTarget(index: number): boolean {
    return this.validationTargetPath?.startsWith(`localizationVariantsForm.${index}.sampleMessages.`) === true;
  }

  get expandHeaderCapabilitiesPanel(): boolean {
    return this.expandedPanel === 'header-capabilities'
      || this.matchesValidationTarget('generalSettings.logo', 'imageComposition.');
  }

  get expandOpeningMessagesPanel(): boolean {
    return this.matchesValidationTarget(
      'generalSettings.introMessage',
      'generalSettings.firstMessage',
      'generalSettings.inputPlaceholder',
      'generalSettings.sampleMessagesClickAnimationDelayMs'
    );
  }

  get expandAssistantAvatarPanel(): boolean {
    return this.matchesValidationTarget('chatAvatar');
  }

  get expandProductCardsValidationPanel(): boolean {
    return this.matchesValidationTarget('productCardSettings.');
  }

  get expandStylingPanel(): boolean {
    return this.matchesValidationTarget('styling.');
  }

  private matchesValidationTarget(...prefixes: string[]): boolean {
    const target = (this.validationTargetPath ?? '').replace(/^frontendForm\./, '');
    return prefixes.some(prefix => target.startsWith(prefix));
  }

  private syncImageCompositionControlState(): void {
    const enabledControl = this.imageCompositionGroup?.get('enabled');
    if (!enabledControl) {
      return;
    }

    if (this.imageCompositionRuntimeReady) {
      enabledControl.enable({ emitEvent: false });
      return;
    }

    enabledControl.setValue(false, { emitEvent: false });
    enabledControl.disable({ emitEvent: false });
  }

  getStylingControl(key: string): FormControl<string | null> {
    return this.stylingGroup.get(key) as FormControl<string | null>;
  }

  getGeneralSettingsControl(key: string): FormControl<string | null> {
    return this.frontendForm.get(`generalSettings.${key}`) as FormControl<string | null>;
  }

  private handleInputShadowToggle(_enabled: boolean): void {
    if (!this.stylingGroup) {
      return;
    }
    // Preserve user-provided styling values; shadow overrides visuals only.
  }

  onHeaderLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    if (!this.isSupportedImageFile(file)) {
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : null;
      if (dataUrl) {
        const control = this.frontendForm.get('generalSettings.logo');
        control?.setValue(dataUrl);
        control?.markAsDirty();
        control?.markAsTouched();
        this.updateHeaderLogoPreview(dataUrl);
      }
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  onChatAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    if (!this.isSupportedImageFile(file)) {
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : null;
      if (dataUrl) {
        const control = this.frontendForm.get('chatAvatar');
        control?.setValue(dataUrl);
        control?.markAsDirty();
        control?.markAsTouched();
        this.updateChatAvatarPreview(dataUrl);
      }
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  private updateHeaderLogoPreview(value: any): void {
    const trimmed = (value ?? '').toString().trim();
    this.headerLogoPreview = trimmed.length > 0 ? trimmed : null;
  }

  private updateChatAvatarPreview(value: any): void {
    const trimmed = (value ?? '').toString().trim();
    this.chatAvatarPreview = trimmed.length > 0 ? trimmed : null;
  }

  private isSupportedImageFile(file: File): boolean {
    return file.type.startsWith('image/') || file.name.toLowerCase().endsWith('.svg');
  }

  private resolveVisibility(rule?: VisibilityRule): boolean {
    if (!rule) {
      return true;
    }
    if (rule.matches) {
      return rule.matches();
    }
    if (!rule.path) {
      return false;
    }
    const control = this.frontendForm.get(rule.path);
    if (!control) {
      return false;
    }
    const value = control.value;
    if (rule.equals !== undefined) {
      return value === rule.equals;
    }
    return !!value;
  }

  private canDisplayControl(control: ColorControlConfig): boolean {
    if (control.requiresAdvanced && !this.isAdvancedStylingEnabled()) {
      return false;
    }
    return this.resolveVisibility(control.visibleWhen);
  }

  isGroupVisible(group: ColorGroupConfig): boolean {
    if (group.requiresAdvanced && !this.isAdvancedStylingEnabled()) {
      return false;
    }

    if (!this.resolveVisibility(group.visibleWhen)) {
      return false;
    }

    return group.controls.some(control => this.canDisplayControl(control));
  }

  isControlVisible(control: ColorControlConfig): boolean {
    return this.canDisplayControl(control);
  }

  colorGroups: ColorGroupConfig[] = [
    {
      title: 'Chat',
      description: 'Applies to AI messages, user replies, and default widget text.',
      controls: [
        { key: 'chatBubbleAssistant', label: 'Assistant Bubble', hint: 'Background color applied to AI responses.' },
        { key: 'chatBubbleUser', label: 'User Bubble', hint: 'Background color applied to user messages.' },
        { key: 'widgetText', label: 'Text', hint: 'Body text and hyperlinks displayed throughout the widget.' },
          {
              key: 'textLinkColor',
              label: 'Rendered Links',
              hint: 'Link color inside rendered text responses and predefined text.'
          },
        { key: 'avatarAccent', label: 'Avatar', hint: 'Accent color for the default star avatar when no custom image is provided.' }
      ]
    },
    {
      title: 'Suggestion Pills',
      description: 'Styles the quick suggestion pills displayed above the composer.',
      controls: [
        { key: 'sugBgColor', label: 'Pills Background', hint: 'Background for each suggestion pill.' },
        { key: 'sugIconBgColor', label: 'Icon Background', hint: 'Background for the icon badge within a pill.' },
        { key: 'sugIconColor', label: 'Icon Color', hint: 'Icon color within the pill.' }
      ]
    },
    {
      title: 'Composer',
      description: 'Controls the message field background and placeholder text.',
      controls: [
        { key: 'inputBackground', label: 'Input Background', hint: 'Background color for the message field.' },
        { key: 'inputPlaceholder', label: 'Placeholder Text', hint: 'Color for placeholder text inside the message field.' }
      ]
    },
    {
      title: 'Buttons & Icons',
      description: 'Controls send, recorder, and close/reset buttons.',
      controls: [
        {
          key: 'ctaPrimaryBackground',
          label: 'Send Background',
          hint: 'Background color for the send button inside the input field.',
          hover: {
            key: 'ctaPrimaryHover',
            label: 'Hover color',
            hint: 'Background when people hover the button.'
          }
        },
        {
          key: 'ctaPrimaryText',
          label: 'Send Icon',
          hint: 'Icon color for the send button inside the input field.',
          hover: {
            key: 'ctaPrimaryTextHover',
            label: 'Hover color',
            hint: 'Text color when people hover any widget button.'
          }
        },
        { key: 'inputBorderColor', label: 'Input Border', hint: 'Default border for the chat input field.' },
        { key: 'inputHoverBorderColor', label: 'Input Hover Border', hint: 'Border when hovering the chat input field.' },
        { key: 'inputActiveBorderColor', label: 'Input Active Border', hint: 'Border when the chat input is focused. Falls back to Input Focus if empty.' },
        { key: 'inputActiveSendBackground', label: 'Active Send Background', hint: 'Send button background when the input is focused.' },
        { key: 'inputActiveSendColor', label: 'Active Send Icon', hint: 'Send button icon color when the input is focused.' },
        {
          key: 'recIconColor',
          label: 'Recorder Icon',
          hint: 'Microphone icon color on the recorder button.',
          requiresAdvanced: true,
          visibleWhen: { path: 'generalSettings.voiceEnabled', equals: true },
          hover: {
            key: 'recIconColorHover',
            label: 'Hover color',
            hint: 'Icon color when the recorder button is hovered.'
          }
        },
        { key: 'resetButton', label: 'Reset Icon', hint: 'Color for the reset conversation action.', requiresAdvanced: true },
        { key: 'resetButtonHoverBackground', label: 'Reset Hover Background', hint: 'Background shown when reset is hovered.', requiresAdvanced: true },
        { key: 'resetButtonHoverColor', label: 'Reset Hover Icon', hint: 'Icon color shown when reset is hovered.', requiresAdvanced: true },
        { key: 'resetButtonPressedBackground', label: 'Reset Pressed Background', hint: 'Background shown while reset is pressed.', requiresAdvanced: true },
        { key: 'resetButtonPressedColor', label: 'Reset Pressed Icon', hint: 'Icon color shown while reset is pressed.', requiresAdvanced: true },
        { key: 'closeButton', label: 'Close Icon & Text', hint: 'Icon or text color for the close button.', requiresAdvanced: true }
      ]
    },
    {
      title: 'Favourites Toggle',
      description: 'Saved items favourite trigger styling.',
      visibleWhen: { path: 'dropdownSettings.dropdownEnabled', equals: true },
      controls: [
        {
          key: 'dpBtnBgColor',
          label: 'Favourite Background',
          hint: 'Background for the saved items favourite button.',
          hover: {
            key: 'dpBtnBgColorHover',
            label: 'Hover color',
            hint: 'Background when the favourite toggle is hovered.'
          }
        },
        {
          key: 'dpBtnColor',
          label: 'Favourite Text & Icon',
          hint: 'Color used for the saved items favourite text and icon.',
          hover: {
            key: 'dpBtnColorHover',
            label: 'Hover color',
            hint: 'Text and icon color when the favourite toggle is hovered.'
          }
        }
      ]
    },
    {
      title: 'Product Cards',
      description: 'Buttons shown inside product cards.',
      visibleWhen: { matches: () => this.aiProductCardsEnabled || this.resolveVisibility({ path: 'productCardSettings.cardType' }) },
      controls: [
        {
          key: 'cardBtnBgColor',
          label: 'Button Background',
          hint: 'Background for buttons shown inside product cards.',
          hover: {
            key: 'cardBtnBgColorHover',
            label: 'Hover color',
            hint: 'Background when product card buttons are hovered.'
          }
        },
        {
          key: 'cardBtnColor',
          label: 'Button Text',
          hint: 'Text color for buttons shown inside product cards.',
          hover: {
            key: 'cardBtnColorHover',
            label: 'Hover color',
            hint: 'Text color when product card buttons are hovered.'
          }
        },
        {
          key: 'cardBtnBorderColor',
          label: 'Button Border',
          hint: 'Border color for buttons shown inside product cards.',
          hover: {
            key: 'cardBtnBorderColorHover',
            label: 'Hover color',
            hint: 'Border color when product card buttons are hovered.'
          }
        },
        {
          key: 'cardAddToCartBgColor',
          label: 'Add to Cart Background',
          hint: 'Background for the add to cart button shown inside product cards.',
          hover: {
            key: 'cardAddToCartBgColorHover',
            label: 'Hover color',
            hint: 'Background when the add to cart button is hovered.'
          }
        },
        {
          key: 'cardAddToCartColor',
          label: 'Add to Cart Text',
          hint: 'Text color for the add to cart button shown inside product cards.',
          hover: {
            key: 'cardAddToCartColorHover',
            label: 'Hover color',
            hint: 'Text color when the add to cart button is hovered.'
          }
        },
        {
          key: 'cardAddToCartBorderColor',
          label: 'Add to Cart Border',
          hint: 'Border color for the add to cart button shown inside product cards.',
          hover: {
            key: 'cardAddToCartBorderColorHover',
            label: 'Hover color',
            hint: 'Border color when the add to cart button is hovered.'
          }
        },
        {
          key: 'cardImageBackgroundColor',
          label: 'Image Background',
          hint: 'Background shown when product images do not fill the image area.'
        },
        {
          key: 'cardPriceCurrentColor',
          label: 'Current Price',
          hint: 'Color for the standard current price when no before-price is shown.'
        },
        {
          key: 'cardPriceSaleCurrentColor',
          label: 'Sale Current Price',
          hint: 'Color for the current sale price when the card also shows an original before-price.'
        },
        {
          key: 'cardPriceOriginalColor',
          label: 'Original Price',
          hint: 'Color for original before-price text.'
        },
        {
          key: 'cardBadgeBackgroundColor',
          label: 'Product Badge Background',
          hint: 'Background for badges rendered inside product cards.'
        },
        {
          key: 'cardBadgeTextColor',
          label: 'Product Badge Text',
          hint: 'Text color for standard product-card badges.'
        },
        {
          key: 'cardSaleBadgeTextColor',
          label: 'Sale Badge Text',
          hint: 'Text color for product-card sale badges.'
        },
        {
          key: 'cardAttributeBackgroundColor',
          label: 'Attribute Background',
          hint: 'Background for size, availability, and vehicle attribute chips.'
        },
        {
          key: 'cardAttributeTextColor',
          label: 'Attribute Text',
          hint: 'Text color for product-card attribute chips.'
        }
      ]
    },
    {
      title: 'Saved Counter Badge',
      description: 'Colors the badge that shows how many items have been saved.',
      visibleWhen: { path: 'dropdownSettings.dropdownEnabled', equals: true },
      controls: [
        { key: 'badgeBackground', label: 'Badge Background', hint: 'Background for the saved counter badge.' },
        { key: 'badgeText', label: 'Badge Text', hint: 'Text color displayed inside the saved counter badge.' }
      ]
    }
  ];

  private isAdvancedStylingEnabled(): boolean {
    const toggle = this.stylingGroup?.get('advancedStylingEnabled');
    if (!toggle) {
      return true;
    }
    return toggle.value === true;
  }

  isAdvancedSectionDisabled(group: ColorGroupConfig): boolean {
    if (!group?.requiresAdvanced) {
      return false;
    }
    return !this.isAdvancedStylingEnabled();
  }

  isControlDisabled(control: ColorControlConfig, group: ColorGroupConfig): boolean {
    if (this.isAdvancedSectionDisabled(group)) {
      return true;
    }
    if (control.requiresAdvanced) {
      return !this.isAdvancedStylingEnabled();
    }
    return false;
  }
}
